// =====================================================================
// Phase 12 — Spouse + Co-Parent Protection (PURE).
// =====================================================================
//
// Codifies the "DO NOT BLOCK VALID SPOUSE ADDITIONS" rules from the
// stabilization phase into ONE module so every spouse / partner / co-
// parent code path (AddGraphMemberModal save, QuickAddModal save,
// SetRelationshipModal, FamilyTreeView "+ Spouse / Partner" tile,
// linkSpouses, repairSpouseEdge, and any future invite-acceptance path
// that brings a partner along) can call the SAME predicate before
// writing the spouse_id edge.
//
// HARD RULES (the user's exact protection contract):
//
//   A spouse / co-parent may be added WITHOUT requiring:
//     • parents          (no parent_ids on either side)
//     • children         (no shared kid yet)
//     • a birthdate
//     • pre-existing family links of any kind
//     • the same last name
//     • the same family branch / side
//
//   Valid spouse / co-parent relationships REMAIN SUPPORTED even when:
//     • the spouse is unrelated by blood
//     • the spouse has no parents added yet
//     • the spouse has no children added yet
//     • the spouse is outside the current family graph branch
//     • the relationship is divorced / remarried / separated /
//       deceased / blended / co-parent only (no marriage)
//
//   Hard invariants:
//     1. spouse / co_parent edges MUST NOT create circular ancestry.
//     2. spouse addition alone MUST NEVER collapse or disconnect the
//        tree.
//     3. spouse addition MUST NEVER auto-create duplicate people.
//     4. spouse / co-parent additions MUST still pass the global
//        duplicate-prevention guardrail.
//     5. adding a spouse MUST preserve existing parent / child edges.
//     6. spouse / co-parent edges are LATERAL relationships, not
//        ancestry edges. parent_ids walks remain primary; spouse_id
//        is display-only beside-pairing data.
//     7. spouse addition SHOULD NOT force generation recalculation
//        beyond the affected branch.
//     8. spouse nodes MAY temporarily exist with minimal information
//        (just a name) safely.
//     9. spouse relationships MUST support the lateral-status taxonomy
//        when those statuses are already partially supported in the
//        UI / data layer.
//
//   If a spouse candidate may already exist, the caller MUST surface
//   the duplicate confirm modal so the user can either reuse the
//   existing person OR create a new node anyway (never silently
//   create another spouse node).
//
// ──────────────────────────────────────────────────────────────────────
// CRITICAL: this module is PURE. No DB calls. No mutations. No React.
// Returns a `SpouseAdditionDecision` so the caller can either:
//   • proceed with its existing addMember + linkSpouses pipeline, OR
//   • surface a confirm / disambiguate modal when a hard rule is
//     about to be violated.
//
// The ACTUAL DB-level guard rails (the bidirectional spouse_id write
// in `linkSpouses`, the parent-child overlap refusal in
// `linkSpouses`, the duplicate guardrail in
// `globalDuplicateGuard.ts`, the cycle guard in `linkParents`) all
// remain in force unchanged — this module is the CLIENT-SIDE
// pre-flight check that prevents an avoidable error from ever
// reaching the database.
//
// ──────────────────────────────────────────────────────────────────────
// Locked-subsystem promise: this file does NOT modify FamilyGraphContext,
// `linkSpouses`, `linkParents`, `repairSpouseEdge`, the kinship engine,
// the spouse / in-law derivation, AddGraphMemberModal, QuickAddModal,
// SetRelationshipModal, FamilyTreeView, GraphCanvas, AuthContext,
// MembershipContext, payment sync, the duplicate guardrail itself, or
// anything in STABILITY.md §1. It is consumed BY existing code only
// when that code is next touched — adding it does not change behavior.
// =====================================================================

import type { GraphMember } from '@/contexts/FamilyGraphContext';
import {
  findDuplicateMatches,
  type DuplicateConfidence,
  type DuplicateMatch,
} from '@/lib/duplicates/duplicateGuard';

// ── Spouse / partner status taxonomy ──────────────────────────────────
//
// The lateral statuses a spouse / co-parent edge MAY carry. Storage of
// these statuses is OUT OF SCOPE for this module — the module just
// declares the supported taxonomy so callers (UI controls, future
// status badges) reference one shared list rather than scattering
// per-component string literals. Some statuses (e.g. 'married',
// 'deceased') are already partially supported via the existing
// `livingStatus` column on the partner row; others ('separated',
// 'divorced', 'co_parent_only') are display-only labels the UI may
// render alongside the heart icon.

export type SpouseStatus =
  | 'married'
  | 'separated'
  | 'divorced'
  | 'deceased_spouse'
  | 'co_parent_only'
  | 'partner'        // unmarried partner / domestic partner
  | 'unspecified';   // default — no lateral status declared

export const SUPPORTED_SPOUSE_STATUSES: SpouseStatus[] = [
  'married',
  'separated',
  'divorced',
  'deceased_spouse',
  'co_parent_only',
  'partner',
  'unspecified',
];

/**
 * True for statuses that imply an active heart-pair render (the heart
 * line + side-by-side pyramid layout). Currently every status except
 * 'co_parent_only' AND 'unspecified-with-no-marriage' renders as a
 * couple. 'co_parent_only' is rendered as a dotted co-parent line
 * (when the renderer adds the dotted variant) — for now treated as
 * lateral but non-couple.
 *
 * Used by future renderers; pure derivation, no side effects.
 */
export const isCoupleRenderingStatus = (s: SpouseStatus): boolean => {
  if (s === 'co_parent_only') return false;
  return true;
};

// ── Decision types ────────────────────────────────────────────────────

export type SpouseBlocker =
  /** The two ids are the same row — cannot self-marry. */
  | 'self-as-spouse'
  /** Adding the edge would put one row in the other's ancestor chain. */
  | 'would-create-circular-ancestry'
  /** A bidirectional spouse_id edge already exists for the partner that
   *  points at someone else and the caller has not opted into replacing
   *  it. The existing `linkSpouses` implementation already handles
   *  re-pairing safely; this blocker fires only in caller-supplied
   *  STRICT mode where the caller wants to refuse a partner-with-prior-
   *  spouse without an explicit "replace" confirmation. */
  | 'partner-already-has-different-spouse'
  /** A `'strong'` duplicate of the proposed new spouse is already in the
   *  graph — the caller MUST surface the duplicate confirm modal and
   *  let the user pick "use existing" vs. "create anyway". */
  | 'strong-duplicate-suspected'
  /** Anchor row was not found in `members`. */
  | 'invalid-anchor'
  /** Caller passed neither a candidateMemberId NOR a candidateName. */
  | 'invalid-candidate';

export type SpouseWarning =
  | 'partner-has-existing-spouse-replaceable'
  | 'possible-duplicate-suspected'
  | 'likely-duplicate-suspected'
  | 'partner-also-listed-as-parent-elsewhere'
  | 'lateral-status-not-yet-supported-in-ui';

export interface SpouseAdditionInput {
  /**
   * The existing relative the new spouse is being attached TO. For an
   * "add spouse of self" flow this is the viewer's canonical row. For
   * an "add Aunt Karen's spouse" flow this is Aunt Karen's row.
   */
  anchorMemberId: string;
  /**
   * If linking to an EXISTING graph row as the new spouse, pass that
   * row's id here. Mutually exclusive with `candidateName` — exactly
   * one must be supplied.
   */
  candidateMemberId?: string | null;
  /**
   * If creating a NEW row for the spouse, pass the typed name here so
   * the duplicate guardrail can run a name scan. Optional birthYear /
   * birthDate sharpen the scan but are NEVER required — minimal-info
   * spouse nodes (just a name) are explicitly supported.
   */
  candidateName?: string | null;
  candidateBirthYear?: number | null;
  candidateBirthDate?: string | null;
  /** All currently-loaded graph members. */
  members: GraphMember[];
  /**
   * Lateral status the user picked. Defaults to 'unspecified'.
   * NEVER affects the decision — surfaced through to the caller so it
   * can be persisted (e.g. into a future `spouse_status` column or a
   * relationship_override badge).
   */
  status?: SpouseStatus;
  /**
   * STRICT mode (caller-side opt-in): when true and the chosen partner
   * already has a different spouse_id link, the helper refuses with
   * `partner-already-has-different-spouse` so the caller can surface a
   * "replace existing spouse?" confirm. Default false — `linkSpouses`
   * already handles the re-pairing path safely.
   */
  strictExistingSpouseCheck?: boolean;
}

export interface SpouseAdditionDecision {
  ok: boolean;
  blocker: SpouseBlocker | null;
  reason: string;
  /** The status echoed back, normalized. */
  status: SpouseStatus;
  /** Soft warnings the caller MAY surface as inline hints. */
  warnings: SpouseWarning[];
  /**
   * If `blocker === 'strong-duplicate-suspected'` (or a soft duplicate
   * warning fired) the suspected matches are returned so the caller
   * can render the existing `<DuplicateConfirmModal />`.
   */
  duplicateMatches: DuplicateMatch[];
  /** Highest duplicate confidence found, for risk-level hints. */
  highestDuplicateConfidence: DuplicateConfidence | null;
  /**
   * When the partner is an EXISTING row that already has a different
   * spouse_id, the existing partner is returned so the caller can
   * render "X is currently linked to Y — replace?" copy.
   */
  partnerCurrentSpouse: GraphMember | null;
  /**
   * Pure-derivation hint: is the candidate row (when an existing row
   * was picked) outside the anchor's ancestry tree entirely? This is
   * EXPECTED for unrelated partners and is NEVER a blocker — surfaced
   * so the caller can render an informational "joining a new branch"
   * note if it wants to.
   */
  isLateralAcrossBranches: boolean;
}

// ── Pure helpers ──────────────────────────────────────────────────────

const REASONS: Record<SpouseBlocker, string> = {
  'self-as-spouse':
    'A person cannot be their own spouse. Pick a different relative or create a new one.',
  'would-create-circular-ancestry':
    'These two people are already linked as parent and child via parent_ids — they cannot also be linked as spouses without breaking the family tree.',
  'partner-already-has-different-spouse':
    'This person is already paired with another spouse. Confirm whether you want to replace the existing pairing before continuing.',
  'strong-duplicate-suspected':
    'A person who looks like the same human is already in the family tree. Use the existing person instead of creating a duplicate.',
  'invalid-anchor':
    'Could not find the existing relative this spouse is being added to. Reload the family tree and try again.',
  'invalid-candidate':
    'Either an existing person OR a typed name is required to add a spouse.',
};

/**
 * Walk parent_ids upward from `startId` to detect whether `targetId`
 * appears anywhere in the ancestor chain. Returns true when target is
 * an ancestor of start (including direct parent). Pure read — no
 * mutation, hard-cap on traversal depth so a corrupted graph cannot
 * loop forever.
 */
const isAncestorOf = (
  members: GraphMember[],
  startId: string,
  targetId: string,
): boolean => {
  if (!startId || !targetId || startId === targetId) return false;
  const byId = new Map(members.map((m) => [m.id, m]));
  const visited = new Set<string>();
  const stack: string[] = [startId];
  let safety = 0;
  while (stack.length > 0 && safety < 5000) {
    safety += 1;
    const cur = stack.pop()!;
    const m = byId.get(cur);
    if (!m) continue;
    for (const pid of m.parentIds ?? []) {
      if (!pid || visited.has(pid)) continue;
      if (pid === targetId) return true;
      visited.add(pid);
      stack.push(pid);
    }
  }
  return false;
};

/**
 * True when linking `aId` and `bId` as spouses would force an
 * ancestry-vs-spouse contradiction — i.e. one is provably the
 * ancestor or descendant of the other via parent_ids. This protects
 * Hard Rule #1.
 *
 * Lateral relationships (siblings, cousins, in-laws, blended-family
 * step-relatives, completely unrelated partners) DO NOT trigger this
 * — the function returns false for any pair that doesn't have a
 * parent_ids ancestry path between them.
 */
export const wouldSpouseEdgeCreateCircularAncestry = (
  members: GraphMember[],
  aId: string,
  bId: string,
): boolean => {
  if (!aId || !bId || aId === bId) return false;
  if (isAncestorOf(members, aId, bId)) return true;
  if (isAncestorOf(members, bId, aId)) return true;
  return false;
};

/**
 * Returns the existing partner row that `memberId` is currently
 * spouse-linked to, when any. Null when unlinked OR when the spouse_id
 * points at a row that no longer exists in the loaded graph.
 */
export const getCurrentSpouse = (
  members: GraphMember[],
  memberId: string,
): GraphMember | null => {
  const m = members.find((x) => x.id === memberId);
  if (!m || !m.spouseId) return null;
  return members.find((x) => x.id === m.spouseId) ?? null;
};

/**
 * Run the duplicate scan against the graph using the candidate's name
 * (and optional birth hints). Wraps the existing pure
 * `findDuplicateMatches` engine so spouse-add flows reuse the same
 * matcher every other graph mutation already uses. Returns [] when
 * the master `duplicateGuardrail` flag is OFF (matching the rest of
 * the system).
 */
export const findExistingSpouseCandidate = (
  members: GraphMember[],
  candidate: {
    name: string;
    birthYear?: number | null;
    birthDate?: string | null;
    /**
     * The anchor relative this new spouse is being attached to — used
     * as a structural hint so a row already married to the same
     * anchor surfaces with high confidence. Optional.
     */
    anchorId?: string | null;
  },
): DuplicateMatch[] => {
  if (!candidate.name || candidate.name.trim().length < 2) return [];
  return findDuplicateMatches({
    name: candidate.name,
    birthYear: candidate.birthYear ?? null,
    birthDate: candidate.birthDate ?? null,
    relationship: 'spouse',
    side: 'self',
    anchorId: candidate.anchorId ?? null,
    secondAnchorId: null,
    email: null,
    members,
  });
};

const highestConfidence = (
  matches: DuplicateMatch[],
): DuplicateConfidence | null => {
  if (matches.some((m) => m.confidence === 'strong')) return 'strong';
  if (matches.some((m) => m.confidence === 'likely')) return 'likely';
  if (matches.some((m) => m.confidence === 'possible')) return 'possible';
  return null;
};

// ── Public API ────────────────────────────────────────────────────────

/**
 * Pure pre-flight check for any spouse / partner / co-parent addition.
 *
 * The function NEVER blocks a valid lateral relationship. The only
 * blockers it produces are:
 *
 *   • `self-as-spouse` — anchor and candidate are the same row.
 *   • `would-create-circular-ancestry` — anchor and candidate are
 *     already in a parent_ids ancestry chain.
 *   • `partner-already-has-different-spouse` — only when the caller
 *     explicitly opts into STRICT mode with `strictExistingSpouseCheck`.
 *   • `strong-duplicate-suspected` — the typed candidate name STRONGLY
 *     matches an already-existing graph row.
 *   • `invalid-anchor` / `invalid-candidate` — input validation.
 *
 * Everything else passes through with `ok: true` plus optional
 * non-blocking warnings.
 *
 * SAFE TO CALL UNCONDITIONALLY — never throws, never mutates, never
 * touches the network. Consumers that don't yet route through this
 * helper continue to work because the existing DB-level guards
 * (`linkSpouses` parent-child overlap refusal + bidirectional verify,
 * `linkParents` cycle guard, the duplicate guardrail) remain in force.
 */
export const evaluateSpouseAddition = (
  input: SpouseAdditionInput,
): SpouseAdditionDecision => {
  const status: SpouseStatus = input.status ?? 'unspecified';
  const members = Array.isArray(input.members) ? input.members : [];
  const warnings: SpouseWarning[] = [];

  // ── Input validation ──
  const anchor = members.find((m) => m.id === input.anchorMemberId);
  if (!anchor) {
    return {
      ok: false,
      blocker: 'invalid-anchor',
      reason: REASONS['invalid-anchor'],
      status,
      warnings,
      duplicateMatches: [],
      highestDuplicateConfidence: null,
      partnerCurrentSpouse: null,
      isLateralAcrossBranches: false,
    };
  }

  const haveExistingPick = !!input.candidateMemberId;
  const haveTypedName =
    !!input.candidateName && input.candidateName.trim().length >= 2;
  if (!haveExistingPick && !haveTypedName) {
    return {
      ok: false,
      blocker: 'invalid-candidate',
      reason: REASONS['invalid-candidate'],
      status,
      warnings,
      duplicateMatches: [],
      highestDuplicateConfidence: null,
      partnerCurrentSpouse: null,
      isLateralAcrossBranches: false,
    };
  }

  // ── Existing-row pick path ──
  if (haveExistingPick) {
    const candidateId = input.candidateMemberId!;

    // Self-as-spouse guard.
    if (candidateId === input.anchorMemberId) {
      return {
        ok: false,
        blocker: 'self-as-spouse',
        reason: REASONS['self-as-spouse'],
        status,
        warnings,
        duplicateMatches: [],
        highestDuplicateConfidence: null,
        partnerCurrentSpouse: null,
        isLateralAcrossBranches: false,
      };
    }

    const candidate = members.find((m) => m.id === candidateId);
    if (!candidate) {
      return {
        ok: false,
        blocker: 'invalid-candidate',
        reason: REASONS['invalid-candidate'],
        status,
        warnings,
        duplicateMatches: [],
        highestDuplicateConfidence: null,
        partnerCurrentSpouse: null,
        isLateralAcrossBranches: false,
      };
    }

    // Hard Rule #1: spouse edge must not create circular ancestry.
    if (
      wouldSpouseEdgeCreateCircularAncestry(
        members,
        input.anchorMemberId,
        candidateId,
      )
    ) {
      return {
        ok: false,
        blocker: 'would-create-circular-ancestry',
        reason: REASONS['would-create-circular-ancestry'],
        status,
        warnings,
        duplicateMatches: [],
        highestDuplicateConfidence: null,
        partnerCurrentSpouse: null,
        isLateralAcrossBranches: false,
      };
    }

    // Existing-spouse-on-partner check. Soft warning by default;
    // hard blocker only when the caller opted into STRICT mode.
    const candidateCurrentSpouse = candidate.spouseId
      ? members.find((m) => m.id === candidate.spouseId) ?? null
      : null;
    const partnerHasDifferentSpouse =
      !!candidateCurrentSpouse &&
      candidateCurrentSpouse.id !== input.anchorMemberId;
    if (partnerHasDifferentSpouse) {
      if (input.strictExistingSpouseCheck) {
        return {
          ok: false,
          blocker: 'partner-already-has-different-spouse',
          reason: REASONS['partner-already-has-different-spouse'],
          status,
          warnings,
          duplicateMatches: [],
          highestDuplicateConfidence: null,
          partnerCurrentSpouse: candidateCurrentSpouse,
          isLateralAcrossBranches: false,
        };
      }
      warnings.push('partner-has-existing-spouse-replaceable');
    }

    // Soft "this row appears in someone else's parent_ids" hint —
    // useful for blended-family situations where the picked partner
    // is also someone's step-parent. Never blocks.
    const isParentElsewhere = members.some((m) =>
      (m.parentIds ?? []).includes(candidateId),
    );
    if (isParentElsewhere) {
      warnings.push('partner-also-listed-as-parent-elsewhere');
    }

    // Lateral-across-branches hint — pure derivation, never blocking.
    const candidateIsAncestorOfAnchor = isAncestorOf(
      members,
      input.anchorMemberId,
      candidateId,
    );
    const anchorIsAncestorOfCandidate = isAncestorOf(
      members,
      candidateId,
      input.anchorMemberId,
    );
    const isLateralAcrossBranches =
      !candidateIsAncestorOfAnchor && !anchorIsAncestorOfCandidate;

    return {
      ok: true,
      blocker: null,
      reason: '',
      status,
      warnings,
      duplicateMatches: [],
      highestDuplicateConfidence: null,
      partnerCurrentSpouse: candidateCurrentSpouse,
      isLateralAcrossBranches,
    };
  }

  // ── New-row creation path (typed name) ──
  const trimmedName = (input.candidateName ?? '').trim();

  // Hard Rule #4: still pass duplicate prevention. We DO NOT block on
  // 'possible' or 'likely' here — those become soft warnings the
  // caller surfaces via `<DuplicateConfirmModal />`. Only a STRONG
  // match (exact name + structural anchor / DOB) blocks, and even
  // then the caller can let the user override via the existing
  // confirm modal — that's the "do not silently create another
  // spouse node" rule.
  const matches = findExistingSpouseCandidate(members, {
    name: trimmedName,
    birthYear: input.candidateBirthYear ?? null,
    birthDate: input.candidateBirthDate ?? null,
    anchorId: input.anchorMemberId,
  });
  const highest = highestConfidence(matches);

  if (highest === 'strong') {
    return {
      ok: false,
      blocker: 'strong-duplicate-suspected',
      reason: REASONS['strong-duplicate-suspected'],
      status,
      warnings,
      duplicateMatches: matches,
      highestDuplicateConfidence: highest,
      partnerCurrentSpouse: null,
      isLateralAcrossBranches: false,
    };
  }
  if (highest === 'likely') warnings.push('likely-duplicate-suspected');
  if (highest === 'possible') warnings.push('possible-duplicate-suspected');

  // Brand-new spouse rows are always lateral (they have no parent_ids
  // yet — the user can fill those in later). Hard Rule #8 — minimal-
  // info spouse nodes are explicitly supported.
  return {
    ok: true,
    blocker: null,
    reason: '',
    status,
    warnings,
    duplicateMatches: matches,
    highestDuplicateConfidence: highest,
    partnerCurrentSpouse: null,
    isLateralAcrossBranches: true,
  };
};

/**
 * Convenience: derive a one-line summary of why the spouse addition
 * was blocked OR what the caller should warn the user about. Returns
 * null when nothing notable. Pure formatting.
 */
export const summarizeSpouseDecision = (
  decision: SpouseAdditionDecision,
): string | null => {
  if (!decision.ok && decision.reason) return decision.reason;
  if (decision.warnings.length === 0) return null;
  if (decision.warnings.includes('partner-has-existing-spouse-replaceable')) {
    return 'This partner is currently linked to someone else. Continuing will replace the existing pairing.';
  }
  if (decision.warnings.includes('likely-duplicate-suspected')) {
    return 'A person with a very similar name is already in the family tree. Review the existing match before saving.';
  }
  if (decision.warnings.includes('possible-duplicate-suspected')) {
    return 'A possibly-similar name is already in the family tree. Review before saving.';
  }
  if (decision.warnings.includes('partner-also-listed-as-parent-elsewhere')) {
    return 'This person is also listed as a parent on another row — that is fine for blended families, just confirming.';
  }
  return null;
};
